/**
 * 类型化 API 客户端。
 *
 * 职责（评审硬要求：不允许静默失败）：
 * 1. 统一基地址（默认 /api/v1，可用 VITE_API_BASE_URL 覆盖）；
 * 2. 自动带 Bearer accessToken；
 * 3. **401 时用 refreshToken 刷新一次并重试原请求**；刷新失败 → 清空会话 + 跳登录 + 可见提示；
 * 4. 统一解析后端信封 {ok:true,data} / {ok:false,error:{code,message,details}}；
 * 5. 网络异常、非信封响应、HTTP 错误都转成 ApiError（带 code/status/details），文案可操作。
 */
import { getAccessToken, getRefreshToken, useSession } from '../store/session.js';
import { toast } from '../store/toast.js';
import { ApiError } from './errors.js';
import type { RefreshDto } from './types.js';

function resolveBase(): string {
  const raw = import.meta.env.VITE_API_BASE_URL;
  const candidate = typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : '/api/v1';
  return candidate.replace(/\/+$/, '');
}

export const API_BASE = resolveBase();

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** App 启动时注册：收到无法恢复的 401 时跳转登录页（默认整页跳转） */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

let unauthorizedNotified = false;

function handleUnauthorized(): void {
  useSession.getState().clear();
  if (!unauthorizedNotified) {
    unauthorizedNotified = true;
    toast.error('登录状态已过期', '请重新登录后继续（未保存的表单内容会丢失）');
    window.setTimeout(() => {
      unauthorizedNotified = false;
    }, 4000);
  }
  if (unauthorizedHandler) {
    unauthorizedHandler();
    return;
  }
  window.location.assign('/login');
}

interface Envelope {
  ok: boolean;
  data: unknown;
  error: { code: string; message: string; details: unknown } | null;
}

function asEnvelope(payload: unknown): Envelope | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.ok !== 'boolean') return null;
  const errorRaw = record.error;
  if (typeof errorRaw === 'object' && errorRaw !== null) {
    const errorRecord = errorRaw as Record<string, unknown>;
    return {
      ok: record.ok,
      data: record.data,
      error: {
        code: typeof errorRecord.code === 'string' ? errorRecord.code : 'INTERNAL',
        message: typeof errorRecord.message === 'string' ? errorRecord.message : '',
        details: errorRecord.details ?? null,
      },
    };
  }
  return { ok: record.ok, data: record.data, error: null };
}

async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function codeForStatus(status: number): string {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  return 'INTERNAL';
}

function httpMessage(response: Response): string {
  if (response.status >= 500) {
    return `服务器异常（HTTP ${response.status}），请稍后重试；若持续出现请把该状态码告诉管理员`;
  }
  if (response.status === 404) {
    return '接口不存在（HTTP 404）：请确认后端已启动且版本与前端一致';
  }
  const suffix = response.statusText.length > 0 ? ` ${response.statusText}` : '';
  return `请求失败（HTTP ${response.status}${suffix}）`;
}

/** 用 refreshToken 换新的 accessToken（不发重试，避免递归） */
async function requestRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return null;
  }
  const envelope = asEnvelope(await readPayload(response));
  if (!response.ok || !envelope || !envelope.ok) return null;
  const data = envelope.data;
  if (typeof data !== 'object' || data === null) return null;
  const refreshLike = data as Partial<RefreshDto>;
  if (typeof refreshLike.accessToken !== 'string' || refreshLike.accessToken.length === 0) return null;
  const rotated = typeof refreshLike.refreshToken === 'string' ? refreshLike.refreshToken : undefined;
  useSession.getState().setTokens(refreshLike.accessToken, rotated);
  return refreshLike.accessToken;
}

let refreshInFlight: Promise<string | null> | null = null;

/** 单飞（single-flight）：并发多个 401 只刷新一次 */
function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * 供启动流程使用：用 refreshToken 换 accessToken。
 * 与 401 重试共用同一条单飞链路，避免 React StrictMode 下重复刷新导致 token 轮换竞态。
 */
export function refreshSession(): Promise<string | null> {
  return refreshAccessToken();
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** false 表示公开接口（登录/注册/刷新），不带 Bearer、不做 401 刷新 */
  auth?: boolean;
}

async function send<T>(path: string, options: RequestOptions, retried: boolean): Promise<T> {
  const method = options.method ?? 'GET';
  const auth = options.auth !== false;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(
      'NETWORK',
      '无法连接后端服务：请确认 API 已启动（默认 http://localhost:3000），并检查 .env 的 VITE_API_BASE_URL',
    );
  }

  if (response.status === 204) return undefined as T;

  if (response.status === 401 && auth && !retried) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return send<T>(path, options, true);
    handleUnauthorized();
    throw new ApiError('UNAUTHORIZED', '登录状态已过期，请重新登录后继续', 401);
  }

  const payload = await readPayload(response);
  const envelope = asEnvelope(payload);

  if (envelope) {
    if (envelope.ok) return envelope.data as T;
    const error = envelope.error;
    throw new ApiError(error?.code ?? codeForStatus(response.status), error?.message ?? '', response.status, error?.details ?? null);
  }

  if (!response.ok) throw new ApiError(codeForStatus(response.status), httpMessage(response), response.status);
  throw new ApiError(
    'BAD_RESPONSE',
    '后端返回了不符合约定的数据格式（缺少 ok 字段），请确认前后端版本一致',
    response.status,
  );
}

export const api = {
  get: <T>(path: string, auth = true): Promise<T> => send<T>(path, { method: 'GET', auth }, false),
  post: <T>(path: string, body?: unknown, auth = true): Promise<T> => send<T>(path, { method: 'POST', body, auth }, false),
  put: <T>(path: string, body?: unknown, auth = true): Promise<T> => send<T>(path, { method: 'PUT', body, auth }, false),
  patch: <T>(path: string, body?: unknown, auth = true): Promise<T> => send<T>(path, { method: 'PATCH', body, auth }, false),
  delete: <T>(path: string, auth = true): Promise<T> => send<T>(path, { method: 'DELETE', auth }, false),
};

/** 拼查询串：忽略 undefined/null/空串，避免出现 `?eventCode=` */
export function queryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text.length > 0 ? `?${text}` : '';
}